import { Button, Card, Cluster, Container, LiveStat, Section, Stack } from "@humaniti/ui";
import { Link } from "react-router-dom";
import { formatCents, formatNumber, useNetworkStats } from "../lib/networkStats";
import { ORGANIZATION_JSONLD, WEBSITE_JSONLD, useSEO } from "../lib/seo";

const PRINCIPLES = [
  {
    n: "01",
    title: "Verified",
    body: "Every label is checked by N peers. Disagreement routes to a Steward. Quality is a protocol feature, not a vendor promise.",
  },
  {
    n: "02",
    title: "Direct pay",
    body: "Humans are paid per verified label, not per hour. No platform skim. No 30-day waits.",
  },
  {
    n: "03",
    title: "Portable",
    body: "Datasets export in 16+ formats. Provenance receipts travel with the work. Your output lives outside our database.",
  },
  {
    n: "04",
    title: "Permanent",
    body: "Every label, every revision, every payout is signed and addressable. Auditable end-to-end. Built to outlast Humaniti the company.",
  },
];

const PHASES = [
  { n: "I", noun: "The Network", tag: "Verified Humans. Verified work. Direct pay." },
  { n: "II", noun: "The Record", tag: "Datasets that don't disappear." },
  { n: "III", noun: "The Economy", tag: "Humans earn equity in the work." },
  { n: "IV", noun: "The Commons", tag: "The network governs itself." },
];

export const HomePage = () => {
  useSEO({
    // Brand-led title for the home only — sub-pages use "Page name | Humaniti".
    // The "Humaniti" prefix means useSEO's decorateTitle leaves it alone (no
    // double-suffix), and the em-dash + sentence reads cleaner than
    // "Datasets, by humans. | Humaniti" (which has a period before the pipe).
    title: "Humaniti — Datasets, by humans.",
    description:
      "Humaniti is a network for verified human-grade datasets. Builders publish tasks, Humans complete them, peer-consensus verifies every label. Pay per verified label, not per hour.",
    path: "/",
    // Site-wide identity signals — Organization + WebSite — only emitted on
    // the home page so search engines + LLMs treat / as the canonical
    // identity surface for Humaniti.
    jsonLd: [ORGANIZATION_JSONLD, WEBSITE_JSONLD],
  });
  const stats = useNetworkStats();
  return (
    <>
      <Section>
        <Container size="full">
          <Stack gap="2xl">
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-label)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-secondary)",
              }}
            >
              § A network for verified human-grade datasets
            </span>
            <h1
              style={{
                margin: 0,
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: "var(--fs-display)",
                lineHeight: "var(--lh-display)",
                letterSpacing: "-0.02em",
                color: "var(--ink)",
                maxWidth: 960,
              }}
            >
              Datasets, by humans<span style={{ color: "var(--teal)" }}>.</span>
            </h1>
            <Stack gap="md" style={{ maxWidth: 620 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--fs-body-lg)",
                  lineHeight: "var(--lh-body-lg)",
                }}
              >
                Humaniti is a network for labeling data. Builders publish tasks. Humans complete
                them. Peer-consensus verifies every label. Builders pay per verified label. Humans
                get paid directly.
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--fs-body-lg)",
                  lineHeight: "var(--lh-body-lg)",
                  color: "var(--fg-secondary)",
                }}
              >
                Verified Humans. Verified work. Direct pay.
              </p>
            </Stack>
            <Cluster gap="lg">
              {/* Until M1.6 launch, every "Hire Humans" / "Earn as a Human" CTA
                  routes through /coming-soon — /app and /annotate sign-ins
                  work but are not advertised publicly. ?for= swaps a single
                  line of copy on the landing so each side reads in context.
                  Repoint these back to /app/sign-in and /annotate/sign-in
                  once both flows are launch-ready. */}
              <Link to="/coming-soon?for=builders" style={{ textDecoration: "none" }}>
                <Button>Hire Humans →</Button>
              </Link>
              <Link to="/coming-soon?for=humans" style={{ textDecoration: "none" }}>
                <Button variant="ghost">Earn as a Human →</Button>
              </Link>
            </Cluster>
          </Stack>
        </Container>
      </Section>

      <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
        <Container size="full">
          <Stack gap="md">
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-label)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-secondary)",
              }}
            >
              § What the Network did
            </span>
            <Cluster gap="2xl" align="baseline">
              <LiveStat label="Humans active" value={formatNumber(stats?.humansActive)} live />
              <LiveStat label="Labels verified" value={formatNumber(stats?.labelsVerifiedTotal)} />
              <LiveStat label="Datasets shipped" value={formatNumber(stats?.datasetsShipped)} />
              <LiveStat label="Direct pay" value={formatCents(stats?.directPayPaidTotalCents)} />
            </Cluster>
          </Stack>
        </Container>
      </Section>

      <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
        <Container size="full">
          <div className="marketing-side-grid marketing-side-grid--narrow">
            <div>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--fs-label)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--fg-secondary)",
                }}
              >
                § 01 — The promise
              </span>
              <h2
                style={{
                  margin: "var(--space-4) 0 0",
                  fontFamily: "var(--font-sans)",
                  fontWeight: 500,
                  fontSize: "var(--fs-h1)",
                  lineHeight: "var(--lh-h1)",
                  letterSpacing: "-0.01em",
                }}
              >
                Four rules<span style={{ color: "var(--teal)" }}>/</span>
                <br />
                No exceptions
              </h2>
            </div>
            <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {PRINCIPLES.map((p) => (
                <li
                  key={p.n}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "64px 1fr",
                    gap: "var(--space-5)",
                    padding: "var(--space-6) 0",
                    borderTop: "1px solid var(--border-hairline)",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--fs-small)",
                      color: "var(--fg-secondary)",
                      paddingTop: "var(--space-1)",
                    }}
                  >
                    {p.n}
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: "var(--font-sans)",
                        fontWeight: 500,
                        fontSize: "22px",
                        lineHeight: "32px",
                        marginBottom: "var(--space-2)",
                      }}
                    >
                      {p.title}.
                    </div>
                    <div
                      style={{
                        fontSize: "var(--fs-body)",
                        lineHeight: "var(--lh-body)",
                        color: "var(--fg-secondary)",
                        maxWidth: 560,
                      }}
                    >
                      {p.body}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Container>
      </Section>

      <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
        <Container size="full">
          <Stack gap="lg">
            <h2
              style={{
                margin: 0,
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: "var(--fs-h1)",
                lineHeight: "var(--lh-h1)",
                letterSpacing: "-0.01em",
              }}
            >
              Pick a side<span style={{ color: "var(--teal)" }}>.</span>
            </h2>
            {/* Two-side preview cards. Two visual rules drive the layout:
                  1. Cards stretch to equal heights — handled by the parent
                     Cluster's default `align-items: stretch` (we deliberately
                     omit the `align` prop; passing `start` would make the
                     shorter card collapse to its content height).
                  2. The "Read more →" link sits at the bottom of each card —
                     each Card is a flex-column with the Link's `margin-top:
                     auto` pushing it to the bottom. So even if one bullet
                     wraps, both Read-more anchors align horizontally. */}
            <Cluster gap="xl">
              {[
                {
                  label: "For Builders",
                  bullets: [
                    "Pay per verified label.",
                    "Datasets you own — exportable in 16+ formats.",
                    "API + SDKs (early access).",
                  ],
                  to: "/for-builders",
                },
                {
                  label: "For Humans",
                  bullets: [
                    "Get paid per verified label.",
                    "Verified accuracy unlocks higher tiers.",
                    "Web + Android. Real anonymity at T1.",
                  ],
                  to: "/for-humans",
                },
              ].map((side) => (
                <Card
                  key={side.label}
                  style={{ flex: 1, minWidth: 320, display: "flex", flexDirection: "column" }}
                >
                  <h3 style={{ margin: 0, fontSize: "var(--fs-h3)" }}>{side.label}</h3>
                  <ul style={{ margin: "var(--space-4) 0 0", paddingLeft: "var(--space-5)" }}>
                    {side.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                  <Link
                    to={side.to}
                    style={{
                      display: "inline-block",
                      marginTop: "auto",
                      paddingTop: "var(--space-5)",
                      alignSelf: "flex-start",
                    }}
                  >
                    Read more →
                  </Link>
                </Card>
              ))}
            </Cluster>
          </Stack>
        </Container>
      </Section>

      <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
        <Container size="full">
          <Stack gap="lg">
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-label)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-secondary)",
              }}
            >
              § This is Phase 1 of 4
            </span>
            <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {PHASES.map((p) => (
                <li
                  key={p.n}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "64px 1fr",
                    gap: "var(--space-5)",
                    padding: "var(--space-4) 0",
                    borderTop: "1px solid var(--border-hairline)",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--fs-small)",
                      color: "var(--teal)",
                    }}
                  >
                    {p.n}
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: "var(--font-sans)",
                        fontWeight: 500,
                        fontSize: "20px",
                      }}
                    >
                      {p.noun}
                      <span style={{ color: "var(--fg-secondary)", fontWeight: 400 }}>
                        {" — "}
                        {p.tag}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
            <Link to="/roadmap">Read the roadmap →</Link>
          </Stack>
        </Container>
      </Section>

      <Section style={{ background: "var(--ink)", color: "var(--paper)" }}>
        <Container size="full">
          <Stack gap="lg" alignItems="center" style={{ textAlign: "center" }}>
            <h2
              style={{
                margin: 0,
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: "var(--fs-h1)",
                lineHeight: "var(--lh-h1)",
                letterSpacing: "-0.01em",
                maxWidth: 720,
              }}
            >
              Build with Humans<span style={{ color: "var(--teal)" }}>.</span>
            </h2>
            <Cluster gap="lg">
              <Link to="/coming-soon?for=builders" style={{ textDecoration: "none" }}>
                <Button>Hire Humans →</Button>
              </Link>
              <Link to="/coming-soon?for=humans" style={{ textDecoration: "none" }}>
                <Button
                  variant="ghost"
                  style={{ color: "var(--paper)", borderColor: "var(--paper)" }}
                >
                  Earn as a Human →
                </Button>
              </Link>
            </Cluster>
          </Stack>
        </Container>
      </Section>
    </>
  );
};
